import { useEffect, useState } from "react";
import { Bank, CheckCircle, FloppyDisk } from "@phosphor-icons/react";
import { bankApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const BANCOS_ECUADOR = [
  "Banco Pichincha", "Banco de Guayaquil", "Banco del Pacífico", "Produbanco",
  "Banco Bolivariano", "Banco Internacional", "Banco ProCredit", "Banco Solidario",
  "Banco del Austro", "BanEcuador", "Coop. JEP", "Coop. Policía Nacional", "Otro",
];

const EMPTY = {
  razon_social: "", ruc: "", banco: "", tipo_cuenta: "Ahorros",
  numero_cuenta: "", titular: "", email_contacto: "", telefono_contacto: "",
};

export default function BankConfigPage() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    bankApi.get()
      .then((d) => { if (d) { setForm({ ...EMPTY, ...d }); setSaved(true); } })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const change = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const handleSave = async () => {
    const required = ["razon_social", "ruc", "banco", "numero_cuenta", "titular"];
    for (const k of required) {
      if (!form[k]) return toast.warning(`Complete el campo ${k.replace("_", " ")}`);
    }
    setSaving(true);
    try {
      await bankApi.save(form);
      toast.success("Datos de recaudo guardados");
      setSaved(true);
    } catch { toast.error("Error guardando"); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="text-muted-foreground">Cargando…</div>;

  return (
    <div className="max-w-3xl space-y-6" data-testid="bank-config-page">
      <div className="bg-card border border-border rounded-md p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-md bg-primary/10 grid place-items-center text-primary">
              <Bank size={24} weight="duotone" />
            </div>
            <div>
              <h3 className="font-heading font-semibold text-xl">Datos de recaudo</h3>
              <p className="text-sm text-muted-foreground">Se incluirán automáticamente en los mensajes de convenio de pago, formateados para copiar y pegar en la app bancaria del deudor.</p>
            </div>
          </div>
          {saved && (
            <div className="flex items-center gap-1.5 text-success text-xs font-medium">
              <CheckCircle size={16} weight="fill" /> Configurado
            </div>
          )}
        </div>
      </div>

      <div className="bg-card border border-border rounded-md p-6 space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <Label htmlFor="razon">Razón social *</Label>
            <Input id="razon" data-testid="input-razon-social" value={form.razon_social} onChange={change("razon_social")} placeholder="Ej: Distribuidora Andina S.A." />
          </div>
          <div>
            <Label htmlFor="ruc">RUC / Cédula *</Label>
            <Input id="ruc" data-testid="input-ruc" value={form.ruc} onChange={change("ruc")} placeholder="1790012345001" />
          </div>

          <div>
            <Label>Banco *</Label>
            <Select value={form.banco} onValueChange={change("banco")}>
              <SelectTrigger data-testid="select-banco"><SelectValue placeholder="Seleccione banco" /></SelectTrigger>
              <SelectContent>
                {BANCOS_ECUADOR.map((b) => (
                  <SelectItem key={b} value={b}>{b}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Tipo de cuenta *</Label>
            <Select value={form.tipo_cuenta} onValueChange={change("tipo_cuenta")}>
              <SelectTrigger data-testid="select-tipo-cuenta"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Ahorros">Ahorros</SelectItem>
                <SelectItem value="Corriente">Corriente</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="cuenta">Número de cuenta *</Label>
            <Input id="cuenta" data-testid="input-numero-cuenta" value={form.numero_cuenta} onChange={change("numero_cuenta")} placeholder="2200123456" />
          </div>
          <div>
            <Label htmlFor="titular">Titular de la cuenta *</Label>
            <Input id="titular" data-testid="input-titular" value={form.titular} onChange={change("titular")} placeholder="Nombre que figura en la cuenta" />
          </div>

          <div>
            <Label htmlFor="email">Email de confirmación</Label>
            <Input id="email" data-testid="input-email" type="email" value={form.email_contacto} onChange={change("email_contacto")} placeholder="pagos@miempresa.ec" />
          </div>
          <div>
            <Label htmlFor="tel">Teléfono contacto</Label>
            <Input id="tel" data-testid="input-telefono" value={form.telefono_contacto} onChange={change("telefono_contacto")} placeholder="02-2345678" />
          </div>
        </div>

        <div className="pt-3 border-t border-border flex justify-end">
          <Button onClick={handleSave} disabled={saving} data-testid="save-bank-btn">
            <FloppyDisk size={16} weight="duotone" className="mr-2" />
            {saving ? "Guardando…" : "Guardar configuración"}
          </Button>
        </div>
      </div>

      <div className="text-xs text-muted-foreground">
        Estos datos se almacenan localmente como configuración global del acreedor. Puede actualizarlos en cualquier momento.
      </div>
    </div>
  );
}
